import React from 'react';
import { usePageContent } from '../../services/contentService';
import LoadingOverlay from '../../components/LoadingOverlay';
import './OurTeam.css';
import SEO from '../../components/SEO';

const OurTeam = () => {
  const { content, loading, error } = usePageContent('our-team');

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: window.innerWidth <= 768 ? '0 1rem' : '0 2rem'
  };

  if (loading || !content) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error loading content: {error}</p>
      </div>
    );
  }

  // Get sections from content
  const heroSection = content?.sections?.find(s => s.type === 'page-hero')?.content;
  const teamSection = content?.sections?.find(s => s.type === 'team-grid')?.content;

  // Get team members from dynamic content or use fallback data
  const teamMembers = teamSection?.teamMembers || [
    {
      name: "Birndze Bete Dzekewong",
      position: "Founder and CEO",
      image: require('../../assets/img/team/Team/Birndze Bete Dzekewong - Founder and CEO.png'),
      bio: "Visionary leader driving youth empowerment across Africa."
    },
    {
      name: "Birla Charity Youngyeh",
      position: "Programs Manager",
      image: require('../../assets/img/team/Team/Birla Charity Y. - Programs lead.png'),
      bio: "Passionate about creating impactful programs for youth development."
    }
  ];

  const TeamCard = ({ member, isLarge = false }) => (
    <div className={`team-card ${isLarge ? 'team-card-large' : ''}`}>
      <div className="team-image-container">
        <img src={member.image} alt={member.name} className="team-image" />
        <div className="team-overlay">
          <div className="team-social">
            <button className="social-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3A2 2 0 0 1 21 5V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V5A2 2 0 0 1 5 3H19M18.5 18.5V13.2A3.26 3.26 0 0 0 15.24 9.94C14.39 9.94 13.4 10.46 12.92 11.24V10.13H10.13V18.5H12.92V13.57C12.92 12.8 13.54 12.17 14.31 12.17A1.4 1.4 0 0 1 15.71 13.57V18.5H18.5M6.88 8.56A1.68 1.68 0 0 0 8.56 6.88C8.56 5.95 7.81 5.19 6.88 5.19S5.19 5.95 5.19 6.88A1.69 1.69 0 0 0 6.88 8.56M8.27 18.5V10.13H5.5V18.5H8.27Z" />
              </svg>
            </button>
            <button className="social-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.46 6C21.69 6.35 20.86 6.58 20 6.69C20.88 6.16 21.56 5.32 21.88 4.31C21.05 4.81 20.13 5.16 19.16 5.36C18.37 4.5 17.26 4 16 4C13.65 4 11.73 5.92 11.73 8.29C11.73 8.63 11.77 8.96 11.84 9.27C8.28 9.09 5.11 7.38 3 4.79C2.63 5.42 2.42 6.16 2.42 6.94C2.42 8.43 3.17 9.75 4.33 10.5C3.62 10.5 2.96 10.3 2.38 10V10C2.38 12.11 3.86 13.85 5.82 14.24C5.46 14.34 5.08 14.39 4.69 14.39C4.42 14.39 4.15 14.36 3.89 14.31C4.43 16 6 17.26 7.89 17.29C6.43 18.45 4.58 19.13 2.56 19.13C2.22 19.13 1.88 19.11 1.54 19.07C3.44 20.29 5.7 21 8.12 21C16 21 20.33 14.46 20.33 8.79C20.33 8.6 20.33 8.42 20.32 8.23C21.16 7.63 21.88 6.87 22.46 6Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="team-info">
        <h3 className="team-name">{member.name}</h3>
        <p className="team-role">{member.role}</p>
        <p className="team-bio">{member.bio}</p>
      </div>
    </div>
  );

  return (
    <div className="our-team-page">
      <SEO
        title="Our Team"
        description="Meet the dedicated professionals, advisors, and creative minds behind YOCILAB."
      />
      {/* Hero Section */}
      <div className="team-hero-section">
        <div style={containerStyle}>
          <div className="team-hero-content">
            <span className="team-section-label">OUR TEAM</span>
            <h1 className="team-hero-title">
              {heroSection?.mainTitle || "Meet the People Behind the Impact"}
            </h1>
            <p className="team-hero-description">
              {heroSection?.description || "Our diverse team of passionate professionals, advisors, and creative minds work together to empower young Africans and drive meaningful change across the continent."}
            </p>
          </div>
        </div>
      </div>

      {/* Core Team Section */}
      <div className="core-team-section">
        <div style={containerStyle}>
          <div className="section-header-center">
            <span className="section-label-orange">{teamSection?.sectionLabel || "OUR TEAM"}</span>
            <h2 className="section-title-center">{teamSection?.mainTitle || "Core Team"}</h2>
            <p className="section-description-center">
              {teamSection?.description || "The dedicated professionals who lead our programs and drive our mission forward every day."}
            </p>
          </div>

          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <TeamCard
                key={index}
                member={{
                  ...member,
                  role: member.position || member.role, // Handle both position and role keys
                  image: member.image || '/assets/team/default-member.jpg' // Fallback image
                }}
                isLarge={index === 0} // Make founder card larger
              />
            ))}
          </div>
        </div>
      </div>

      {/* Join Our Team CTA */}
      <div className="join-team-section">
        <div style={containerStyle}>
          <div className="join-team-content">
            <h2 className="join-team-title">Want to Join Our Team?</h2>
            <p className="join-team-description">
              We're always looking for passionate individuals who share our vision
              of empowering young Africans. Explore opportunities to make a difference.
            </p>
            <div className="join-team-buttons">
              <button className="careers-btn">View Open Positions</button>
              <button className="volunteer-btn">Volunteer With Us</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurTeam;